import { useState } from 'react';
import { Modal } from '../../components/Modal';
import { isPlanError } from '../../components/plan/planErrors';
import { UpsellFromError } from '../../components/plan/Upsell';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type { GenerationRequestDetailRead } from '../../types/api';
import { setRequestTheme } from '../generation/generationApi';
import { ThemeGallery } from '../generation/ThemeGallery';
import { themeBySlug, themesForVibe, useSlideThemes } from '../generation/useSlideThemes';
import { AI_TEXT_NOTE } from './LayerEditor';

/** Message for the domain errors a look change can provoke. */
function switchMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'REQUEST_NOT_EDITABLE':
        return 'This post is no longer waiting for your approval, so its visual style is fixed. Reloading it now.';
      case 'THEME_NOT_FOUND':
        return 'That visual style is not in the catalogue any more. Pick another one.';
      case 'SLIDE_TEXT_IN_IMAGE':
      case 'slide_text_in_image':
        return AI_TEXT_NOTE;
      default:
        return caught.message;
    }
  }
  return getErrorMessage(caught, 'That visual style did not apply. Try again.');
}

interface ThemeSwitcherProps {
  businessId: string;
  request: GenerationRequestDetailRead;
  /** True while any slide is still being drawn — a re-render cannot queue on one. */
  isRendering: boolean;
  /** The re-themed request, with every slide back to `rendered_path: null`. */
  onChanged: (next: GenerationRequestDetailRead) => void;
  /** The request moved on without us; reload it. */
  onStale: () => void;
  /**
   * `button` draws only the Change look control (the current look named on it)
   * for a review action bar; `row` is the labelled row with the look's blurb.
   */
  variant?: 'row' | 'button';
}

/**
 * The deck's current look, and the control that changes it.
 *
 * Re-rendering an existing deck in another theme is the same class of action as
 * a layer edit: the words are already written and already approved, so this
 * redraws them and never calls a model or spends quota. The caller polls the
 * slides back in exactly as it does after an edit — the response comes back with
 * every `rendered_path` null, which is the poll's own trigger.
 *
 * The grid is filtered by the vibe the run was briefed with, falling back to the
 * whole catalogue: an empty gallery here would leave the user unable to change
 * the look at all, which is a worse answer than showing a look the vibe tags
 * did not predict.
 *
 * The current look is matched on slug rather than id, because a run that picked
 * its theme automatically carries no `theme_id` — `slides[0].theme.name` is the
 * only record of what was actually drawn.
 */
export function ThemeSwitcher({
  businessId,
  request,
  isRendering,
  onChanged,
  onStale,
  variant = 'row',
}: ThemeSwitcherProps) {
  const { themes } = useSlideThemes(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A 402: the plan has no post editor. Shown beside the trigger, modal closed.
  const [planError, setPlanError] = useState<ApiError | null>(null);

  const currentSlug = request.slides[0]?.theme.name ?? null;
  const current = themeBySlug(themes, currentSlug);
  const matched = themesForVibe(themes, request.vibe_id);
  // The deck's own look always has a card, even when its tags say another vibe
  // owns it: a grid that cannot show what is already on screen cannot be read.
  const offered =
    current && !matched.some((theme) => theme.id === current.id) ? [current, ...matched] : matched;

  async function apply(themeId: string) {
    if (isSaving) return;

    setError(null);
    setPlanError(null);
    setIsSaving(true);
    try {
      const next = await setRequestTheme(businessId, request.id, themeId);
      onChanged(next);
      setIsOpen(false);
    } catch (caught) {
      if (isPlanError(caught)) {
        setPlanError(caught);
        setIsOpen(false);
        return;
      }
      setError(switchMessage(caught));
      if (caught instanceof ApiError && caught.code === 'REQUEST_NOT_EDITABLE') {
        setIsOpen(false);
        onStale();
      }
    } finally {
      setIsSaving(false);
    }
  }

  const currentName = current?.name ?? currentSlug ?? 'Classic';
  const isCompact = variant === 'button';
  const renderingNote = 'Available once the slides have finished rendering.';

  const trigger = (
    <button
      className={isCompact ? 'button look-button' : 'button'}
      type="button"
      disabled={isRendering || isSaving || themes.length === 0}
      title={isCompact ? (isRendering ? renderingNote : current?.description) : undefined}
      onClick={() => {
        setError(null);
        setIsOpen(true);
      }}
    >
      {isSaving ? (
        'Applying'
      ) : isCompact ? (
        <>
          Change style <span className="look-button__name">{currentName}</span>
        </>
      ) : (
        'Change visual style'
      )}
    </button>
  );

  return (
    <div className={isCompact ? 'look-control' : 'look-row'}>
      {isCompact ? null : (
        <p className="look-row__current">
          <span className="look-row__label">Visual style</span>
          <span className="look-row__name">{currentName}</span>
          {current ? <span className="look-row__blurb">{current.description}</span> : null}
        </p>
      )}

      {trigger}

      {isRendering && !isCompact ? <span className="look-row__note">{renderingNote}</span> : null}

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      <UpsellFromError error={planError} />

      {isOpen ? (
        <Modal title="Change the visual style" kind="look" onDismiss={() => setIsOpen(false)}>
          <p className="modal__message">
            The same words, drawn again in another visual style. Every slide is redrawn, and the
            band and card tweaks you made on individual slides go back to what the new style says.
            No AI is used and it does not use a post.
          </p>

          <ThemeGallery
            themes={offered}
            selectedId={current?.id ?? null}
            onSelect={(themeId) => {
              if (themeId === null || themeId === current?.id) {
                setIsOpen(false);
                return;
              }
              void apply(themeId);
            }}
            compact
            disabled={isSaving}
          />

          {error ? (
            <p className="form__error" role="alert">
              {error}
            </p>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}
